import React, { createContext, useMemo, useContext, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../authentication';
import { useAgenticGetChatsQuery } from '../hooks/useAgenticGetChatsQuery';
import { useOrganizations } from '../../organization/providers';
import { Conversation } from '../models';

const AgenticConversationsContext = createContext();

const AgenticConversationsProvider = ({ children = null }) => {
  // #region State
  const [groupedConversations, setGroupedConversations] = useState({});
  const [conversations, setConversations] = useState([]);

  // #region Hooks
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const chatsQuery = useAgenticGetChatsQuery(accessToken, currentOrganization?.id);

  // #region Callbacks and Functions
  const groupConversations = useCallback(
    (_conversations) => {
      const updatedGroups = {};

      // Get current date and define date boundaries
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      const startOfWeek = new Date(today);
      // Adjust if your week starts on Monday
      startOfWeek.setDate(today.getDate() - today.getDay());

      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      _conversations.forEach((conversation) => {
        const updatedAt = new Date(conversation.updatedAt);

        if (updatedAt >= today) {
          updatedGroups.Today = updatedGroups.Today || [];
          updatedGroups.Today.push(conversation);
        } else if (updatedAt >= yesterday && updatedAt < today) {
          updatedGroups.Yesterday = updatedGroups.Yesterday || [];
          updatedGroups.Yesterday.push(conversation);
        } else if (updatedAt >= startOfWeek && updatedAt < yesterday) {
          updatedGroups['This week'] = updatedGroups['This week'] || [];
          updatedGroups['This week'].push(conversation);
        } else if (updatedAt >= startOfMonth && updatedAt < startOfWeek) {
          updatedGroups['This month'] = updatedGroups['This month'] || [];
          updatedGroups['This month'].push(conversation);
        } else {
          const monthYear = updatedAt.toLocaleString('default', {
            month: 'long',
            year: 'numeric'
          });
          updatedGroups[monthYear] = updatedGroups[monthYear] || [];
          updatedGroups[monthYear].push(conversation);
        }
      });

      setGroupedConversations(updatedGroups);
    },
    [] // Empty dependency array to prevent infinite loops
  );

  const openNextConversation = useCallback(
    (id, openNew = false) => {
      if (openNew) {
        return navigate('/agentic-chats/new');
      }

      if (conversations.length > 1) {
        const currentIndex = conversations.findIndex((conv) => conv.id === id);
        if (currentIndex !== -1) {
          const nextIndex = currentIndex === 0 ? 1 : 0;
          return navigate(`/agentic-chats/${conversations[nextIndex].id}`);
        }
      }

      return navigate('/agentic-chats/new');
    },
    [conversations, navigate]
  );

  const deleteConversation = useCallback(async () => {
    // TODO: Implement delete functionality for agentic chats
    // eslint-disable-next-line no-console
    console.warn('Delete agentic chat not implemented yet');
  }, []);

  const updateConversation = useCallback(async () => {
    // TODO: Implement update functionality for agentic chats
    // eslint-disable-next-line no-console
    console.warn('Update agentic chat not implemented yet');
  }, []);

  const fetchNextConversationsPage = useCallback(() => {
    // No pagination needed with useQuery
  }, []);

  // #endregion

  // Transform agentic chats to match the expected conversation format
  useEffect(() => {
    if (!chatsQuery.data || !Array.isArray(chatsQuery.data)) return;

    const allConversations = chatsQuery.data.map(
      (chat) =>
        new Conversation({
          id: chat.id,
          title: chat.title || 'New agentic chat',
          updatedAt: chat.updatedAt,
          createdAt: chat.createdAt,
          shares: [], // Empty array for no sharing
          organizationId: currentOrganization?.id,
          type: 'agentic' // Add type for agentic chats
        })
    );

    groupConversations(allConversations);
    setConversations(allConversations);
  }, [chatsQuery.data, currentOrganization?.id]); // Removed groupConversations from dependency array

  const value = useMemo(
    () => ({
      groupedConversations,
      conversations,
      fetchNextConversationsPage,
      refetch: chatsQuery.refetch,
      deleteConversation,
      updateConversation,
      openNextConversation
    }),
    [
      groupedConversations,
      conversations,
      fetchNextConversationsPage,
      chatsQuery.refetch,
      deleteConversation,
      updateConversation,
      openNextConversation
    ]
  );

  if (chatsQuery.isLoading) return null;

  return (
    <AgenticConversationsContext.Provider value={value}>
      {children}
    </AgenticConversationsContext.Provider>
  );
};

AgenticConversationsProvider.propTypes = {
  children: PropTypes.node
};

const useAgenticConversations = () => useContext(AgenticConversationsContext);

export { AgenticConversationsProvider, AgenticConversationsContext, useAgenticConversations };
